/**
 * 치과가 보는 지원 건 상태 — 글자·색 갈래·거르기 칩 분류(순수 함수, 화면 부품 없음).
 * 화면은 applicantStatus.tsx 를 통해서만 쓴다(알약·칩이 같은 기준을 써서 칩 숫자와 행 글자가 항상 맞게).
 * 단위 테스트: dental-clinic-project/tests/applicantStatusModel.test.ts (`node --test`).
 *
 * 대화방 단계가 있으면 단계가 먼저, 없거나 APPLIED 거나 모르는 값이면 지원서 상태(APPLY·PASS·DROP·CANCEL).
 * 단계 이름은 대화방 쪽에서 아직 늘어나는 중이라 문자열로 받는다.
 */

export type ApplicantStatusTone = 'apply' | 'pass' | 'closed' | 'danger';

/** 치과가 보는 '아직 결과를 정하지 않은 지원'(지원서 APPLY·대화방 지원완료 단계) 글자 — 지원자 화면은 그대로 '지원완료' */
export const CLINIC_NEW_APPLY_LABEL: string = '새 지원';

export interface ApplicantStatusView {
    label: string;
    tone: ApplicantStatusTone;
}

/** 지원자 관리 상태 거르기 칩 — ALL 은 전체 */
export type ApplicantStatusGroup = 'APPLY' | 'IN_PROGRESS' | 'WORKED' | 'CLOSED' | 'NO_SHOW';
export type ApplicantStatusFilter = 'ALL' | ApplicantStatusGroup;

/** 칩 순서·이름(폰·PC 같음, 0건 칩도 보인다) */
export const APPLICANT_STATUS_FILTERS: ReadonlyArray<{ value: ApplicantStatusFilter; name: string }> = [
    { value: 'ALL', name: '전체' },
    // 치과 화면 글자 — 알약(clinicStatusOf)과 같은 '새 지원'(2026-10-24 채용 설문 시안)
    { value: 'APPLY', name: CLINIC_NEW_APPLY_LABEL },
    { value: 'IN_PROGRESS', name: '진행 중' },
    { value: 'WORKED', name: '근무 완료' },
    { value: 'CLOSED', name: '불합격·거절' },
    { value: 'NO_SHOW', name: '노쇼' },
];

interface StageInfo extends ApplicantStatusView {
    group: ApplicantStatusGroup;
}

const APPLY_STATUS: Record<string, StageInfo> = {
    APPLY: { label: '지원완료', tone: 'apply', group: 'APPLY' },
    // 대화방 전에 합격 처리된 건 — 근무 일정이 아직이라 진행 중
    PASS: { label: '합격', tone: 'pass', group: 'IN_PROGRESS' },
    DROP: { label: '불합격', tone: 'closed', group: 'CLOSED' },
    CANCEL: { label: '지원 취소', tone: 'closed', group: 'CLOSED' },
};

const STAGE: Record<string, StageInfo> = {
    INTERVIEW_PROPOSED: { label: '면접 요청', tone: 'apply', group: 'IN_PROGRESS' },
    INTERVIEW_CONFIRMED: { label: '면접 확정', tone: 'pass', group: 'IN_PROGRESS' },
    // 최종 합격, 출근일 보내기 전(docs/hire-close-plan.md 5절 — 대화방 칩과 같은 글자)
    HIRED: { label: '출근일 조율', tone: 'pass', group: 'IN_PROGRESS' },
    OFFERED: { label: '확정 대기', tone: 'apply', group: 'IN_PROGRESS' },
    CONFIRMED: { label: '근무 확정', tone: 'pass', group: 'IN_PROGRESS' },
    DECLINED: { label: '거절', tone: 'closed', group: 'CLOSED' },
    EXPIRED: { label: '기한 지남', tone: 'closed', group: 'CLOSED' },
    DROPPED: { label: '불합격', tone: 'closed', group: 'CLOSED' },
    WORKED: { label: '근무 완료', tone: 'pass', group: 'WORKED' },
    NO_SHOW: { label: '노쇼', tone: 'danger', group: 'NO_SHOW' },
    INTERVIEW_NO_SHOW: { label: '면접 불참', tone: 'danger', group: 'NO_SHOW' },
    CLINIC_CANCELLED: { label: '치과 취소', tone: 'closed', group: 'CLOSED' },
    // 지원자 입사 포기(알바는 '근무 취소' — offerKind 로 가른다, withdrawLabel). 노쇼 아님
    WITHDRAWN: { label: '입사 포기', tone: 'closed', group: 'CLOSED' },
    // 약속 기록이 기록 없음·확인 안 된 취소로 끝남(대화방 칩과 같은 "끝남")
    PROMISE_CLOSED: { label: '끝남', tone: 'closed', group: 'CLOSED' },
};

/**
 * 입사 포기(정규·계약 — 출근 START, 또는 근무 조건 없음) / 근무 취소(알바 — 근무 WORK·첫 근무일 FIRST_DAY).
 * 대화방 threadFormat.withdrawLabelOf·서버 ApplicationThreadRules.withdrawWord 와 같은 규칙.
 */
export const withdrawLabel = (offerKind?: string | null): string => (offerKind && offerKind !== 'START' ? '근무 취소' : '입사 포기');

/** 이의를 낼 수 있는 단계 — 이의가 있으면 글자 뒤에 "· 이의 제기됨" */
const DISPUTABLE: ReadonlySet<string> = new Set(['NO_SHOW', 'INTERVIEW_NO_SHOW']);

/** 노쇼 이의 처리 결과(어드민) — ACCEPTED 면 노쇼가 취소된 것 */
export type DisputeResolution = 'ACCEPTED' | 'REJECTED';

/** 이의가 받아들여져 취소된 노쇼 — 회색 알약, 어느 칩에도 넣지 않고 '전체'에만 센다 */
const CANCELLED_NO_SHOW: Record<string, string> = { NO_SHOW: '노쇼 취소됨', INTERVIEW_NO_SHOW: '불참 취소됨' };

const isCancelledNoShow = (stage?: string | null, disputeResolution?: string | null): boolean =>
    disputeResolution === 'ACCEPTED' && !!stage && stage in CANCELLED_NO_SHOW;

/** 단계(APPLIED 제외·아는 값만) → 없으면 지원서 상태 → 그것도 모르면 null */
const resolve = (applyStatus: string, stage?: string | null): StageInfo | null =>
    (stage ? STAGE[stage] : undefined) ?? APPLY_STATUS[applyStatus] ?? null;

/** 지원서 상태 글자만 */
export const applyStatusLabel = (applyStatus: string): string => APPLY_STATUS[applyStatus]?.label ?? applyStatus;

/**
 * 상태 알약 글자·색. [disputeResolution] ACCEPTED 면 "노쇼 취소됨"/"불참 취소됨"(회색),
 * REJECTED 면 원래 글자(빨강, "이의 제기됨" 꼬리 없음 — 처리가 끝났다).
 */
export const applicantStatusOf = (
    applyStatus: string,
    stage?: string | null,
    disputed?: boolean,
    disputeResolution?: string | null,
    /** 대화방 근무 조건 종류 — WITHDRAWN 이면 입사 포기 / 근무 취소를 가른다 */
    offerKind?: string | null,
): ApplicantStatusView => {
    if (isCancelledNoShow(stage, disputeResolution)) return { label: CANCELLED_NO_SHOW[stage as string], tone: 'closed' };
    if (stage === 'WITHDRAWN') return { label: withdrawLabel(offerKind), tone: 'closed' };
    // 정규·계약 출근 확인 뒤 — 대화방 칩과 같은 "출근 확정"
    if (stage === 'CONFIRMED' && offerKind === 'START') return { label: '출근 확정', tone: 'pass' };
    const info: StageInfo | null = resolve(applyStatus, stage);
    if (!info) return { label: applyStatus, tone: 'closed' };
    const pending: boolean = !!disputed && !disputeResolution;
    const label: string = pending && stage && STAGE[stage] && DISPUTABLE.has(stage) ? `${info.label} · 이의 제기됨` : info.label;
    // 색 규칙(대화방 세션과 합의, 2026-10-06): 확정된 건 좋음 · 상대 답을 기다리는 건 중립 · 끝난 건 끝남 · 노쇼만 빨강.
    // 노쇼·면접 불참이라도 이의 제기 중(처리 전)이면 중립.
    const tone: ApplicantStatusTone = pending && stage && DISPUTABLE.has(stage) ? 'apply' : info.tone;
    return { label, tone };
};

/**
 * 치과 화면(지원자 관리 목록·지원자 이력서) 알약 — [applicantStatusOf] 와 같고, 지원서 APPLY(대화방 단계 없음·지원완료 단계)만
 * '새 지원'으로 부른다(서버 상태 APPLY 와 1:1, 채용 설문 39번 대응 시안). 지원자 화면(지원 내역)은 [statusOf] 그대로 '지원완료'.
 */
export const clinicStatusOf = (
    applyStatus: string,
    stage?: string | null,
    disputed?: boolean,
    disputeResolution?: string | null,
    offerKind?: string | null,
): ApplicantStatusView => {
    const view: ApplicantStatusView = applicantStatusOf(applyStatus, stage, disputed, disputeResolution, offerKind);
    return resolve(applyStatus, stage) === APPLY_STATUS.APPLY ? { ...view, label: CLINIC_NEW_APPLY_LABEL } : view;
};

/** 대화방 협의 문서의 이름·인자 순서 — [applicantStatusOf] 와 같다 */
export const statusOf = (stage: string | null | undefined, applyStatus: string, disputed?: boolean, disputeResolution?: string | null, offerKind?: string | null): ApplicantStatusView =>
    applicantStatusOf(applyStatus, stage, disputed, disputeResolution, offerKind);

/**
 * 거르기 칩 갈래 — 알약과 같은 기준. 이의가 받아들여진 노쇼는 null(어느 칩에도 없고 '전체'에만 센다).
 * 지원서 상태까지 모르면 불합격·거절.
 */
export const applicantStatusGroupOf = (applyStatus: string, stage?: string | null, disputeResolution?: string | null): ApplicantStatusGroup | null =>
    isCancelledNoShow(stage, disputeResolution) ? null : resolve(applyStatus, stage)?.group ?? 'CLOSED';

/** 칩이 세는 한 줄 */
export interface ApplicantStatusRow {
    applyStatus: string;
    stage?: string | null;
    disputeResolution?: string | null;
}

/** 칩 숫자 — 전체와 갈래별(0건 갈래도 0으로) */
export const countApplicantGroups = (rows: ReadonlyArray<ApplicantStatusRow>): Record<ApplicantStatusFilter, number> => {
    const counts: Record<ApplicantStatusFilter, number> = { ALL: rows.length, APPLY: 0, IN_PROGRESS: 0, WORKED: 0, CLOSED: 0, NO_SHOW: 0 };
    rows.forEach((row) => {
        const group: ApplicantStatusGroup | null = applicantStatusGroupOf(row.applyStatus, row.stage, row.disputeResolution);
        if (group) counts[group] += 1;
    });
    return counts;
};

/** 칩으로 거른 목록 */
export const filterByApplicantGroup = <T extends ApplicantStatusRow>(rows: T[], filter: ApplicantStatusFilter): T[] =>
    filter === 'ALL' ? rows : rows.filter((row) => applicantStatusGroupOf(row.applyStatus, row.stage, row.disputeResolution) === filter);

/** 칩 이름 */
export const applicantFilterName = (filter: ApplicantStatusFilter): string =>
    APPLICANT_STATUS_FILTERS.find((item) => item.value === filter)?.name ?? filter;
